import { ArrowLeft2, Message } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import ProfileAddMultiple from '@/assets/images/icons/profile-add-multiple.svg?react';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useGroup } from '@/hooks/contacts/use-groups';
import useJoinGroup from '@/hooks/groups/use-join-group';
import { useGroupByCode } from '@/hooks/scan-code/use-group-by-code';
import ROUTES from '@/routes';
import { addToast } from '@/stores/toast';

export default function ContactRequestGroupRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ code }] = useTypedSearchParams(ROUTES.CONTACT.REQUEST.GROUP);

  const { data, isLoading: isGroupLoading } = useGroupByCode(code);
  const { isJoined } = useGroup(data?.GroupData.ID);
  const { mutateAsync: applyJoin, isPending: isApplyingToJoin } = useJoinGroup(true);

  const handleApply = async () => {
    if (!data?.GroupData.ID) return Promise.reject();

    const result = await applyJoin({ groupId: data.GroupData.ID });

    if (result.Code === 0) {
      addToast({
        type: 'success',
        message: result.Msg,
      });

      navigate(ROUTES.MESSAGES.$path(), { replace: true });
    }
  };

  const isLoading = isGroupLoading || isApplyingToJoin;

  return (
    <PageLayout
      className='relative !pb-0'
      title={isLoading ? t('contact.scan-code.join-group') : data?.GroupData.GroupName}
      bannerColor='dark'>
      <button
        className='btn btn-square btn-ghost absolute left-4 top-safe-offset-4 z-20 hover:shadow'
        onClick={() => navigate(-1)}>
        <ArrowLeft2
          size={24}
          variant='Outline'
          className='aspect-square size-6 text-slate-200'
        />
      </button>

      <LoadingLayout promise={isGroupLoading}>
        {() => (
          <div
            className='flex size-full flex-1 flex-col gap-4 bg-cover bg-center bg-no-repeat'
            style={{ backgroundImage: `url("${data?.GroupData.GroupImg}")` }}>
            <div className='flex flex-1 flex-col items-stretch bg-neutral-950/50 px-5 text-neutral-50 backdrop-blur-sm pb-safe-offset-8'>
              <div className='flex flex-1 flex-col items-center justify-center'>
                <AvatarWithStatus
                  className='size-32'
                  size={[128, 128]}
                  imageUrl={data?.GroupData.GroupImg}
                  label={data?.GroupData.GroupName}
                  defaultAvatar='group'
                />

                <h2 className='mt-6 text-h2 desktop:text-sm'>{data?.GroupData.GroupName ?? ''}</h2>
              </div>

              {data?.GroupData.ID && isJoined ? (
                <Link
                  className='btn btn-primary'
                  to={ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id: data.GroupData.ID } })}>
                  <Message className='size-5' />

                  {t('contact.scan-code.join-group-chat')}
                </Link>
              ) : (
                <button
                  type='button'
                  disabled={isApplyingToJoin}
                  className='btn btn-primary'
                  onClick={handleApply}>
                  {isApplyingToJoin && <span className='loading loading-spinner' />}
                  <ProfileAddMultiple className='size-5' />
                  {t('contact.scan-code.join-group')}
                </button>
              )}
            </div>
          </div>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
